import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { BottomSheet, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { useLeaveClipboardGuidesChannel } from "../api/useLeaveClipboardGuidesChannel";
import { ChatOptionsBottomSheet } from "./ChatOptionsBottomSheet";

interface LeaveClipboardGuidesChannelButtonProps {
  workplaceId: string;
  onLeft: () => void;
}

export function LeaveClipboardGuidesChannelButton(
  props: LeaveClipboardGuidesChannelButtonProps
): JSX.Element {
  const { workplaceId, onLeft } = props;
  const worker = useDefinedWorker();
  const optionsModalState = useModalState();
  const confirmModalState = useModalState();
  const { showErrorToast, showSuccessToast } = useToast();

  const { mutateAsync: leaveChannel, isLoading: isLeavingChannel } = useLeaveClipboardGuidesChannel(
    { workplaceId }
  );

  return (
    <>
      <IconButton
        variant="outlined"
        iconType="ellipsis"
        aria-label="Chat options"
        onClick={optionsModalState.openModal}
      />
      <ChatOptionsBottomSheet
        modalState={optionsModalState}
        onLeaveChat={confirmModalState.openModal}
      />
      <BottomSheet
        modalState={confirmModalState}
        footer={
          <DialogFooter orientation="vertical">
            <Button
              fullWidth
              variant="destructive"
              size="large"
              disabled={isLeavingChannel}
              onClick={async () => {
                // Navigate away before awaiting the mutation so the Sendbird
                // Channel component unmounts before its `onUserLeft` event
                // fires — otherwise the SDK re-renders with a null channel
                // and crashes MessageContentWithLongPress.
                onLeft();
                try {
                  await leaveChannel({ workerId: worker.userId });
                  showSuccessToast("You've left the chat.");
                } catch {
                  // Error is automatically logged via the mutation's meta.logErrorMessage.
                  showErrorToast("Something went wrong. Please try again.");
                }
              }}
            >
              Leave chat
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={isLeavingChannel}
              onClick={confirmModalState.closeModal}
            >
              Cancel
            </Button>
          </DialogFooter>
        }
      >
        <Stack spacing={4} sx={{ padding: 6, paddingTop: 8 }}>
          <Text variant="h3">Leave chat?</Text>
          <Text>
            You&apos;ll stop receiving messages and notifications from this community chat, and you
            won&apos;t be added back automatically when you work more shifts here.
          </Text>
        </Stack>
      </BottomSheet>
    </>
  );
}
